'use client'

import { Badge, Button } from '@mentora/ui-components'
import { useTranslations } from 'next-intl'

export type StoreAdminsPanelRole = 'user' | 'storeAdmin' | 'platformAdmin'

export interface StoreAdminsPanelUser {
  id: string
  name: string
  email: string
  role: StoreAdminsPanelRole
}

export interface StoreUserRowProps {
  user: StoreAdminsPanelUser
  isCurrentUser: boolean
  disabled: boolean
  onChange: (user: StoreAdminsPanelUser, grant: boolean) => void
}

/** One person: who they are, their store role, and the one change allowed on them. */
export function StoreUserRow({ user, isCurrentUser, disabled, onChange }: StoreUserRowProps) {
  const t = useTranslations('storeAdmins')
  // A Platform Admin is changed nowhere on this screen, and nobody changes their own role.
  const canChange = user.role !== 'platformAdmin' && !isCurrentUser

  return (
    <li className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">
          {user.name}
          {isCurrentUser && <span className="ml-1 text-muted-foreground">{t('you')}</span>}
        </p>
        <p className="truncate text-sm text-muted-foreground">{user.email}</p>
      </div>
      <div className="flex items-center gap-2">
        {user.role !== 'user' && (
          <Badge variant={user.role === 'platformAdmin' ? 'default' : 'secondary'}>
            {t(user.role === 'platformAdmin' ? 'roles.platformAdmin' : 'roles.storeAdmin')}
          </Badge>
        )}
        {canChange && (
          <Button
            size="sm"
            variant={user.role === 'storeAdmin' ? 'outline' : 'default'}
            disabled={disabled}
            onClick={() => onChange(user, user.role !== 'storeAdmin')}
            data-testid={`store-admin-toggle-${user.id}`}
          >
            {t(user.role === 'storeAdmin' ? 'revoke' : 'grant')}
          </Button>
        )}
      </div>
    </li>
  )
}
